<template>
  <div class="my-2 rounded-lg border">
    <div class="my-2 grid grid-cols-1 gap-4 p-2 sm:grid-cols-6 md:col-span-2">
      <StdPropEditor
        v-for="prop in confluenceIntegrationProps"
        :key="prop.name"
        :prop-def="prop"
        :class="[getPropLayoutClasses(prop)]"
        :model-value="configurationData[prop.name]"
        @update:model-value="
          (newValue) => {
            onUpdateProp(prop, newValue);
          }
        "
      />
    </div>
    <Message v-if="errorMessage !== ''" class="m-2" severity="error" :sticky="false">{{ errorMessage }}</Message>
    <ProgressDialog ref="integrationsOpProgress"></ProgressDialog>
    <div class="m-2 flex items-center justify-end">
      <button
        type="button"
        class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
        @click="onCancel"
      >
        Cancel
      </button>
      <button type="button" class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto" @click="onSave">Save</button>
    </div>
  </div>
</template>
<script setup>
  import { ref, reactive, onMounted } from 'vue';
  import StdPropEditor from '@/components/Editors/StdPropEditor.vue';
  import { useApi } from '@/hooks/useApi';
  import { useSettingsStore } from '@/store/SettingsStore';
  import Message from 'primevue/message';
  import { useToast } from 'vue-toastification';
  import ProgressDialog from '@/components/Dialogs/ProgressDialog.vue';

  const emit = defineEmits(['canceled', 'saved']);

  const settingsStore = useSettingsStore();

  const errorMessage = ref('');
  const integrationsOpProgress = ref(null);
  const toast = useToast();

  let configurationData = reactive({
    subdomain: '',
    access_token: '',
    api_token_email: '',
  });

  const confluenceIntegrationProps = [
    { name: 'subdomain', type: 'string', title: 'Subdomain', default: '' },
    { name: 'api_token_email', type: 'string', title: 'Username (your email address)', default: '' },
    { name: 'access_token', type: 'string', title: 'Password (API Key)', default: '' },
  ];

  const propLayoutClassesByType = {
    text: 'col-span-full',
    boolean: 'col-span-full',
    number: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    string: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-2',
    select: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    stringLong: 'col-span-full',
    audioFile: 'col-span-full',
    transcriptsCollection: 'col-span-full',
    recordingsCollection: 'col-span-full',
    kpiCollection: 'col-span-full',
    categoriesCollection: 'col-span-full',
    temperature: 'col-span-full',
  };

  function getPropLayoutClasses(prop) {
    return propLayoutClassesByType[prop?.type] || '';
  }

  function onUpdateProp(prop, newValue) {
    console.log(`onUpdateProp: ${prop.name} = "${newValue}"`);
    configurationData[prop.name] = newValue;
    //emit('update:modelValue', arrayValue);
  }

  function validateConfiguration() {
    if (!configurationData.subdomain || configurationData.subdomain === '') {
      return {
        valid: false,
        message: 'Please provide value for Subdomain',
      };
    }
    if (!configurationData.access_token || configurationData.access_token === '') {
      return {
        valid: false,
        message: 'Please provide value for Password(API Key)',
      };
    }
    if (!configurationData.api_token_email || configurationData.api_token_email === '') {
      return {
        valid: false,
        message: 'Please provide value for Username(email address)',
      };
    }

    return { valid: true };
  }

  async function onSave() {
    errorMessage.value = '';
    const validateResult = validateConfiguration();
    if (!validateResult.valid) {
      errorMessage.value = validateResult.message;
      return;
    }
    const api = useApi();

    // extract subdomain, if full Portal URL provided
    configurationData.subdomain = configurationData.subdomain.replace(/^https?:\/\//, '').split('.')[0];

    integrationsOpProgress.value.show({
      title: 'Enable Confluence Integration',
      description: `Validating Credentials and Subdomain`,
      message: `Creating Integrations...`,
    });
    let provisionTenantResult = await api.confluenceProvisionTenant(configurationData);
    if (!provisionTenantResult.success) {
      integrationsOpProgress.value.showError({ message: `${provisionTenantResult.error}` });
      return;
    }
    integrationsOpProgress.value.hide();
    await settingsStore.setIntegrationEnabled('confluence', true);

    toast.success(`Confluence Integration Enabled`);
  }

  function onCancel() {
    emit('canceled');
  }

  onMounted(async () => {
    const config = await settingsStore.getIntegrationSettings('confluence');
    if (!config || typeof config !== 'object') {
      return;
    }
    Object.keys(config).map((x) => {
      configurationData[x] = config[x];
    });
  });
</script>
